import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import "./SherlocMediaCampaigns.css";
import "./CreativeOptimus.css";
import "./CreativeStudio.css";
import "./CreativeTrafficking.css";
import "./CreativeFamily.css";
import "./CreativeDesktopWidth.css";
import CphereHeaderLogo from "../components/CphereHeaderLogo";
import {
  AdvertiserIdentityTrigger,
  GovernedAdvertiserSelectorModal,
} from "../components/GovernedAdvertiserSelector";
import useGovernedAdvertiserCatalog from "../hooks/useGovernedAdvertiserCatalog";
import {
  normalizeCreativeLiveDeliveryMap,
  mergeCreativeMetrics,
  getPrimaryMetricPresentation,
  hasLiveDeliverySignal,
  describeContributionKpi,
  normalizeUnresolvedCreativeDelivery,
  pendingLinkPlatformKeysFor,
} from "../utils/creativeDeliverySignal";
import {
  buildApprovedPlanContext,
  compareCreativeLibraryRowsByPlatform,
  compareCreativePlatformKeys,
  normalizePlatformKey,
  platformLabelFromKey,
  resolveCreativeStudioAssetMeta,
} from "../utils/creativeStudioIntelligence";
import facebookHero from "../assets/facebook.jpg";
import instagramHero from "../assets/instagram.jpg";
import tiktokHero from "../assets/tiktok.jpg";
import xHero from "../assets/x.jpg";
import youtubeHero from "../assets/youtube.jpg";
import googleSearchHero from "../assets/google.jpg";
import rumbleHero from "../assets/rumble-logo.jpg";
import truthSocialHero from "../assets/truth-social-logo.jpg";
import redditHero from "../assets/reddit.jpg";
import amazonHero from "../assets/amazon.jpg";
import viantHero from "../assets/viant.png";
import ctvHero from "../assets/ctv.jpg";
import videoHero from "../assets/video.jpg";
import displayHero from "../assets/display.jpg";
import mobileHero from "../assets/mobile.jpg";
import audioHero from "../assets/audio.jpg";
import ingameHero from "../assets/ingame.jpg";
import doohHero from "../assets/dooh.gif";
import emailHero from "../assets/email.jpg";
import {
  inferCreativeType,
  getCreativeMimeType,
  getCreativeThumbnailUrl,
  getCreativeAssetStreamPaths,
  getCreativeAssetUrl,
  getCreativeAspectRatio,
  resolveCreativePreviewSourceUrl,
} from "../utils/creativeAssetPreview";
import CreativePreviewTile from "../components/CreativeAssetPreviewTile";
import { filterClientVisibleCreativeAssets } from "../utils/creativeAssetRole";

const normalizeBase = (base) => {
  if (!base) return "";
  let next = String(base).trim();
  next = next.replace(/\/+$/, "");
  if (next.toLowerCase().endsWith("/api")) next = next.slice(0, -4);
  return next;
};

const API_BASE_URL =
  normalizeBase(process.env.REACT_APP_BACKEND_URL) ||
  normalizeBase(process.env.REACT_APP_BACKEND) ||
  normalizeBase(process.env.REACT_APP_API_URL) ||
  "http://localhost:5000";

const PLATFORM_HERO_ART = {
  facebook: { src: facebookHero, label: "Facebook" },
  instagram: { src: instagramHero, label: "Instagram" },
  tiktok: { src: tiktokHero, label: "TikTok" },
  x: { src: xHero, label: "X" },
  youtube: { src: youtubeHero, label: "YouTube" },
  google_search: { src: googleSearchHero, label: "Google Search" },
  rumble: { src: rumbleHero, label: "Rumble" },
  truth_social: { src: truthSocialHero, label: "Truth Social" },
  reddit: { src: redditHero, label: "Reddit" },
  amazon: { src: amazonHero, label: "Amazon" },
  viant: { src: viantHero, label: "Viant", fit: "wordmark" },
  ctv: { src: ctvHero, label: "CTV" },
  video: { src: videoHero, label: "Video" },
  display: { src: displayHero, label: "Display" },
  mobile: { src: mobileHero, label: "Mobile" },
  audio: { src: audioHero, label: "Audio" },
  ingame: { src: ingameHero, label: "In-Game" },
  dooh: { src: doohHero, label: "DOOH" },
  email: { src: emailHero, label: "Email" },
};

function getAuthToken() {
  return (
    localStorage.getItem("clientAuthToken") ||
    localStorage.getItem("superadminToken") ||
    localStorage.getItem("authToken") ||
    ""
  );
}

async function apiJson(path, options = {}) {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method || "GET",
    signal: options.signal,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    credentials: "include",
  });
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(json?.error || json?.detail || `Request failed (${response.status})`);
  }
  return json;
}

const normalizeText = (value) => String(value || "").trim().toLowerCase();
const toAssetKey = (value) => String(value ?? "").trim();

function normalizeLifecycleBucket(value) {
  const normalized = String(value || "").trim().toUpperCase();
  if (!normalized) return "DRAFT";
  if (normalized.includes("ARCHIVE")) return "ARCHIVED";
  if (normalized.includes("APPROVED")) return "APPROVED";
  if (normalized.includes("CHANGE")) return "CHANGES_REQUESTED";
  if (normalized.includes("REVIEW")) return "IN_REVIEW";
  return "DRAFT";
}

function isApprovedAssetForLibrary(asset) {
  if (!asset || asset?.archived_at) return false;
  if (normalizeLifecycleBucket(asset?.lifecycle_status || asset?.status) === "APPROVED") return true;
  return Boolean(asset?.approved_version_id || asset?.approvedVersion?.id);
}

function getCreativeEffectiveStatusLabel(asset) {
  if (isApprovedAssetForLibrary(asset)) return "Approved";
  return normalizeStatusLabel(asset?.lifecycle_status || asset?.status, "Draft");
}

async function apiJsonOptional(path, fallbackValue, options = {}) {
  try {
    return await apiJson(path, options);
  } catch (error) {
    if (options.signal?.aborted || error?.name === "AbortError") throw error;
    console.warn("[CreativeTrafficking] optional request failed:", path, error?.message || error);
    return fallbackValue;
  }
}

function normalizeStatusLabel(value, fallback = "Not Set") {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizePlatformLabel(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  const labels = {
    facebook: "Meta",
    meta: "Meta",
    instagram: "Instagram",
    tiktok: "TikTok",
    google: "Google",
    google_display: "Google Display",
    google_search: "Google Search",
    youtube: "YouTube",
    x: "X",
    twitter: "X",
    display: "Display",
    ctv: "CTV",
  };

  return labels[normalized] || normalizeStatusLabel(normalized, "Unknown");
}

function getPlatformHeroVisual(platformKey) {
  return PLATFORM_HERO_ART[String(platformKey || "").trim()] || null;
}

function CreativeStudioPlatformHeroes({
  platformKeys = [],
  platformLabels = [],
  fallback = "—",
  compact = false,
}) {
  const normalized = Array.from(
    new Set(
      (Array.isArray(platformKeys) ? platformKeys : [])
        .map((key) => String(key || "").trim())
        .filter(Boolean)
    )
  );
  const resolved = normalized
    .map((key) => ({
      key,
      label: platformLabelFromKey(key) || platformLabels.find((entry) => entry === key) || key,
      visual: getPlatformHeroVisual(key),
    }))
    .filter((entry) => entry.label);

  if (!resolved.length) {
    return <span className="creativestudio-platform-fallback">{fallback}</span>;
  }

  return (
    <div className={`creativestudio-platform-hero-list ${compact ? "is-compact" : ""}`}>
      {resolved.map((platform) => {
        const fallbackText = platform.label.slice(0, 2).toUpperCase();
        return (
          <span
            key={platform.key}
            className={`creativestudio-platform-hero-chip ${compact ? "is-compact" : ""}`}
            title={platform.label}
            aria-label={platform.label}
          >
            <span
              className={`creativestudio-platform-hero-frame ${compact ? "is-compact" : ""}`}
              aria-hidden="true"
            >
              <span className="creativestudio-platform-hero-media">
                {platform.visual?.src ? (
                  <img
                    src={platform.visual.src}
                    alt=""
                    className={
                      platform.visual.fit === "wordmark"
                        ? "creativestudio-platform-hero-image creativestudio-platform-hero-image--wordmark"
                        : "creativestudio-platform-hero-image"
                    }
                    draggable="false"
                  />
                ) : (
                  <span className="creativestudio-platform-hero-fallback">{fallbackText}</span>
                )}
              </span>
            </span>
            <span className="creativestudio-platform-hero-label">{platform.label}</span>
          </span>
        );
      })}
    </div>
  );
}

function pickNumber(...values) {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
}

function formatAspectRatioLabel(width, height, fallbackValue = "") {
  const explicit = String(fallbackValue || "").trim();
  if (explicit) return explicit.replace(/\s+/g, "");
  if (!width || !height) return "Flexible";
  const gcd = (left, right) => (right ? gcd(right, left % right) : left);
  const divisor = gcd(Math.round(width), Math.round(height)) || 1;
  return `${Math.round(width / divisor)}:${Math.round(height / divisor)}`;
}

function parseDimensionsFromText(...values) {
  for (const value of values) {
    const raw = String(value || "");
    const match = raw.match(/(\d{2,5})\s*[x×]\s*(\d{2,5})/i);
    if (match) {
      const width = Number(match[1]);
      const height = Number(match[2]);
      if (Number.isFinite(width) && Number.isFinite(height)) {
        return { width, height };
      }
    }
  }
  return { width: null, height: null };
}

function getCreativeSizing(asset) {
  const metadata = asset?.metadata || {};
  const versionMetadata = asset?.approvedVersion?.metadata || asset?.currentVersion?.metadata || {};
  let width = pickNumber(
    asset?.width,
    asset?.asset_width,
    asset?.dimensions?.width,
    metadata?.width,
    metadata?.videoWidth,
    metadata?.imageWidth,
    metadata?.naturalWidth,
    metadata?.pixelWidth,
    versionMetadata?.width,
    versionMetadata?.videoWidth,
    asset?.approvedVersion?.width,
    asset?.currentVersion?.width
  );
  let height = pickNumber(
    asset?.height,
    asset?.asset_height,
    asset?.dimensions?.height,
    metadata?.height,
    metadata?.videoHeight,
    metadata?.imageHeight,
    metadata?.naturalHeight,
    metadata?.pixelHeight,
    versionMetadata?.height,
    versionMetadata?.videoHeight,
    asset?.approvedVersion?.height,
    asset?.currentVersion?.height
  );

  if (!width || !height) {
    const parsed = parseDimensionsFromText(
      asset?.title,
      asset?.file_name,
      asset?.approvedVersion?.file_name,
      asset?.currentVersion?.file_name,
      metadata?.resolution,
      metadata?.dimensions,
      versionMetadata?.resolution,
      versionMetadata?.dimensions
    );
    width = width || parsed.width;
    height = height || parsed.height;
  }

  const roundedWidth = width ? Math.round(width) : null;
  const roundedHeight = height ? Math.round(height) : null;
  const aspectRatio = formatAspectRatioLabel(
    roundedWidth,
    roundedHeight,
    asset?.aspect_ratio || metadata?.aspect_ratio || versionMetadata?.aspect_ratio
  );
  const orientation =
    roundedWidth && roundedHeight
      ? roundedWidth === roundedHeight
        ? "Square"
        : roundedWidth > roundedHeight
        ? "Landscape"
        : "Portrait"
      : "Flexible";

  return {
    width: roundedWidth,
    height: roundedHeight,
    aspectRatio,
    orientation,
    isFlexible: !(roundedWidth && roundedHeight),
    display: roundedWidth && roundedHeight ? `${roundedWidth} x ${roundedHeight}` : "Flexible",
  };
}

function getCreativeApprovalLabel(asset) {
  const lifecycle = normalizeLifecycleBucket(asset?.lifecycle_status || asset?.status);
  if (lifecycle === "APPROVED") return "Approved";
  if (asset?.approvalSummary?.fullyApproved) return "Approved";
  if (asset?.approvalSummary) {
    return `Superadmin ${normalizeStatusLabel(asset.approvalSummary.superadmin, "Pending")} · Client ${normalizeStatusLabel(
      asset.approvalSummary.client,
      "Pending"
    )}`;
  }
  return normalizeStatusLabel(asset?.lifecycle_status || asset?.status, "Not available");
}

function getCreativeMetrics(asset) {
  const summary =
    asset?.performanceSummary ||
    asset?.performance_summary ||
    asset?.metrics ||
    asset?.metricSummary ||
    asset?.metric_summary ||
    asset?.latestMetrics ||
    asset?.latest_metrics ||
    asset?.deliveryMetrics ||
    asset?.delivery_metrics ||
    asset?.analytics ||
    {};

  return {
    spend: pickNumber(summary?.spend, summary?.amount_spent, summary?.mediaSpend, asset?.spend),
    impressions: pickNumber(summary?.impressions, asset?.impressions),
    ctr: pickNumber(summary?.ctr, summary?.ctr_percent, asset?.ctr, asset?.ctr_percent),
    vtr: pickNumber(summary?.vtr, summary?.vtr_percent, summary?.video_view_rate, summary?.video_completion_rate),
    engagement: pickNumber(
      summary?.engagement_rate,
      summary?.engagementRate,
      summary?.engagement_rate_percent,
      summary?.engagementRatePercent,
      asset?.engagement_rate
    ),
  };
}

function getAssetLastUpdated(asset) {
  return (
    asset?.updatedAt ||
    asset?.updated_at ||
    asset?.createdAt ||
    asset?.created_at ||
    null
  );
}

function buildInventoryRows({
  assets,
  advertiserName,
  packageDetailsById,
  draftDetailsById,
  approvedPlatformKeys = [],
  liveDeliveryByAssetId = {},
  unresolvedCreativeDelivery = [],
}) {
  const approvedPlatformKeySet = new Set((Array.isArray(approvedPlatformKeys) ? approvedPlatformKeys : []).filter(Boolean));
  const hasApprovedPlanPlatforms = approvedPlatformKeySet.size > 0;
  const approvedPlatformLabelSet = new Set(
    (Array.isArray(approvedPlatformKeys) ? approvedPlatformKeys : [])
      .map((key) => normalizeText(platformLabelFromKey(key)))
      .filter(Boolean)
  );
  const entryPlatformKey = (entry) => normalizePlatformKey(entry?.platformKey || entry?.platformLabel);
  const entryPlatformLabel = (entry) => platformLabelFromKey(entryPlatformKey(entry)) || String(entry?.platformLabel || "").trim();
  const filterToApprovedPlatforms = (entries = []) =>
    hasApprovedPlanPlatforms
      ? entries.filter((entry) => {
          const normalizedKey = entryPlatformKey(entry);
          const normalizedLabel = normalizeText(entry?.platformLabel);
          return approvedPlatformKeySet.has(normalizedKey) || approvedPlatformLabelSet.has(normalizedLabel);
        })
      : [];
  const packageDetails = Object.values(packageDetailsById || {}).filter(Boolean);
  const draftDetails = Object.values(draftDetailsById || {}).filter(Boolean);

  const packageItemById = new Map();
  packageDetails.forEach((detail) => {
    const pkg = detail?.package || {};
    (detail?.items || []).forEach((item) => {
      if (!item?.id) return;
      packageItemById.set(String(item.id), {
        creativeAssetId: Number(item.creativeAssetId),
        platform: item.platform || pkg.platform || null,
        packageName: pkg.name || null,
      });
    });
  });

  const platformEntriesByAsset = new Map();
  const pushPlatformEntry = (creativeAssetId, entry) => {
    const assetKey = String(creativeAssetId || "");
    if (!assetKey) return;
    const next = platformEntriesByAsset.get(assetKey) || [];
    next.push(entry);
    platformEntriesByAsset.set(assetKey, next);
  };

  packageDetails.forEach((detail) => {
    const pkg = detail?.package || {};
    (detail?.items || []).forEach((item) => {
      if (!item?.creativeAssetId) return;
      pushPlatformEntry(item.creativeAssetId, {
        platformLabel: normalizePlatformLabel(item.platform || pkg.platform),
        stateLabel: pkg.validationResults?.valid === true ? "Prepared" : "Packaged",
        stateRank: pkg.validationResults?.valid === true ? 1 : 0,
        packageName: pkg.name || null,
      });
    });
  });

  draftDetails.forEach((detail) => {
    const draft = detail?.draft || {};
    (detail?.items || []).forEach((item) => {
      const packageItem =
        packageItemById.get(String(item?.traffickingPackageItemId || "")) || null;
      const creativeAssetId =
        Number(item?.creativeAssetId) ||
        Number(packageItem?.creativeAssetId) ||
        null;
      if (!creativeAssetId) return;

      const platformLabel = normalizePlatformLabel(
        item?.platform ||
          item?.platformPayload?.platform ||
          packageItem?.platform ||
          draft.platform
      );
      const approvalState = String(draft.approvalState || "").toUpperCase();
      let stateLabel = "Drafted";
      let stateRank = 2;

      if (approvalState === "APPROVED") {
        stateLabel = "Approved Draft";
        stateRank = 3;
      } else if (approvalState === "REJECTED") {
        stateLabel = "Exception";
        stateRank = 2;
      } else if (approvalState === "PENDING_REVIEW") {
        stateLabel = "Draft Review";
        stateRank = 2;
      }

      pushPlatformEntry(creativeAssetId, {
        platformLabel,
        stateLabel,
        stateRank,
        packageName: packageItem?.packageName || draft.name || null,
      });
    });
  });

  return (Array.isArray(assets) ? assets : [])
    .map((asset) => {
      const rawEntries = platformEntriesByAsset.get(String(asset.id)) || [];
      const platformMap = new Map();

      rawEntries.forEach((entry) => {
        const key = String(entry.platformLabel || "").trim();
        if (!key) return;
        const existing = platformMap.get(key);
        if (!existing || (entry.stateRank || 0) > (existing.stateRank || 0)) {
          platformMap.set(key, entry);
        }
      });

      const mappedDistribution = filterToApprovedPlatforms(Array.from(platformMap.values()));
      const inferredStudioMeta = resolveCreativeStudioAssetMeta(asset, { approvedPlatformKeys });
      const inferredDistribution = hasApprovedPlanPlatforms
        ? (inferredStudioMeta?.compatiblePlatformKeys || []).map((platformKey) => ({
            platformKey,
            platformLabel: platformLabelFromKey(platformKey),
            stateLabel: "Mapped",
            stateRank: -1,
            packageName: "Derived from approved media plan alignment",
            isInferred: true,
          }))
        : [];
      const resolvedDistributionMap = new Map();
      [...mappedDistribution, ...inferredDistribution].forEach((entry) => {
        const normalizedKey = entryPlatformKey(entry);
        if (!normalizedKey) return;
        const existing = resolvedDistributionMap.get(normalizedKey);
        if (!existing) {
          resolvedDistributionMap.set(normalizedKey, {
            ...entry,
            platformKey: normalizedKey,
          });
          return;
        }
        if (existing.isInferred && !entry.isInferred) {
          resolvedDistributionMap.set(normalizedKey, {
            ...existing,
            ...entry,
            platformKey: normalizedKey,
          });
        }
      });
      const resolvedDistribution = Array.from(resolvedDistributionMap.values()).sort((left, right) =>
        compareCreativePlatformKeys(entryPlatformKey(left), entryPlatformKey(right))
      );
      const sizing = getCreativeSizing(asset);

      return {
        assetId: asset.id,
        asset,
        assetName: asset.title || asset.file_name || `Asset ${asset.id}`,
        advertiser: advertiserName || "Advertiser",
        creativeType: inferCreativeType(asset),
        creativeFormat: asset.format || asset.mime_type || "Not set",
        dimensions: sizing.display,
        width: sizing.width,
        height: sizing.height,
        aspectRatio: sizing.aspectRatio,
        orientation: sizing.orientation,
        isFlexible: sizing.isFlexible,
        approvalLabel: getCreativeApprovalLabel(asset),
        status: getCreativeEffectiveStatusLabel(asset),
        distribution: resolvedDistribution,
        platformLabels: resolvedDistribution.map((entry) => entryPlatformLabel(entry)).filter(Boolean),
        platformKeys: resolvedDistribution.length
          ? resolvedDistribution
              .map((entry) => entryPlatformKey(entry))
              .filter(Boolean)
          : hasApprovedPlanPlatforms
            ? inferredStudioMeta?.compatiblePlatformKeys || []
            : [],
        thumbnailUrl: getCreativeThumbnailUrl(asset),
        assetUrl: getCreativeAssetUrl(asset),
        previewMimeType: getCreativeMimeType(asset),
        metrics: (() => {
          const merged = mergeCreativeMetrics(
            getCreativeMetrics(asset),
            liveDeliveryByAssetId[String(asset.id)] || null
          );
          const boundPlatformKeys = resolvedDistribution.length
            ? resolvedDistribution.map((entry) => entryPlatformKey(entry)).filter(Boolean)
            : hasApprovedPlanPlatforms
              ? inferredStudioMeta?.compatiblePlatformKeys || []
              : [];
          /*
           * Unlinked results on a platform this creative is bound to: the
           * creative may have delivered, but nothing can say so until the
           * link is verified. Stamped here so the KPI pill can name that
           * condition instead of "Awaiting delivery".
           */
          const pendingLinkPlatformKeys = pendingLinkPlatformKeysFor(boundPlatformKeys, unresolvedCreativeDelivery)
            .filter((key) => !(merged.deliveryPlatformKeys || []).includes(key));
          return { ...merged, pendingLinkPlatformKeys };
        })(),
        versionLabel:
          asset?.currentVersion?.version_label ||
          asset?.version ||
          "v1",
        lastUpdated: getAssetLastUpdated(asset),
      };
    })
    // Creative Studio's canonical platform sequence, applied to whole rows:
    // platform position first, recency only within a platform group.
    .sort(compareCreativeLibraryRowsByPlatform);
}


function formatDate(value) {
  if (!value) return "Not set";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not set" : date.toLocaleString();
}

function formatCurrencyCompact(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: value >= 100 ? 0 : 2,
  }).format(value);
}

function formatIntegerCompact(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value);
}

function getStatusToneClass(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "is-neutral";
  if (
    ["approved", "approved draft", "prepared", "live", "active", "validated", "validation passed"].includes(
      normalized
    )
  ) {
    return "is-positive";
  }
  if (
    ["exception", "rejected", "archived", "error", "failed"].includes(normalized)
  ) {
    return "is-negative";
  }
  if (
    ["draft", "drafted", "draft review", "pending review", "packaged", "needs validation"].includes(
      normalized
    )
  ) {
    return "is-attention";
  }
  return "is-neutral";
}

function getStatusChipClass(value) {
  const tone = getStatusToneClass(value);
  return tone ? `creativetrafficking-status-chip ${tone}` : "creativetrafficking-status-chip";
}


/*
 * Cortex Creative Assets — the client-authorized creative library: every
 * governed creative with its attributed delivery per platform (StackAdapt
 * CTV/Display and linked YouTube results) and the selected creative's detail.
 * Trafficking packages, deployment plans and analysis are operator surfaces
 * governed by CREATIVE_BETA_INTERNAL_ACCESS and are not rendered here; the
 * shared library markup, styles and delivery-signal util are the same ones
 * Quantum uses, so the two portals never disagree about a figure.
 */
const CreativeAssets = () => {
  const navigate = useNavigate();

  const [assets, setAssets] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageDetailsById, setPackageDetailsById] = useState({});
  const [draftDetailsById, setDraftDetailsById] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [creativeTypeFilter, setCreativeTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [updatedFrom, setUpdatedFrom] = useState("");
  const [updatedTo, setUpdatedTo] = useState("");
  const [liveOnly, setLiveOnly] = useState(false);
  /*
   * Retained delivery that resolved to no governed creative (a YouTube ad
   * name with no verified identity mapping yet). It is disclosed beside the
   * library rather than attributed to the nearest-looking asset.
   */
  const [unresolvedCreativeDelivery, setUnresolvedCreativeDelivery] = useState([]);
  const [selectedInventoryAssetId, setSelectedInventoryAssetId] = useState("");
  const [previewModalAssetId, setPreviewModalAssetId] = useState("");
  const [approvedPlanCampaigns, setApprovedPlanCampaigns] = useState([]);
  const [liveCreativeDeliveryByAssetId, setLiveCreativeDeliveryByAssetId] = useState({});
  const advertiserScopeEpochRef = useRef(0);
  const packageDetailEpochRef = useRef(0);
  const draftDetailEpochRef = useRef(0);

  const clearAdvertiserScopedState = useCallback(() => {
    advertiserScopeEpochRef.current += 1;
    packageDetailEpochRef.current += 1;
    draftDetailEpochRef.current += 1;
    setAssets([]);
    setPackages([]);
    setApprovedPlanCampaigns([]);
    setLiveCreativeDeliveryByAssetId({});
    setPackageDetailsById({});
    setDraftDetailsById({});
    setSelectedInventoryAssetId("");
    setPreviewModalAssetId("");
    setError("");
  }, []);

  const {
    selectedAdvertiserId,
    selectedAdvertiserName,
    visibleAdvertisers: advertisers,
    currentAdvertiser: resolvedAdvertiser,
    loading: advertisersLoading,
    error: advertiserCatalogError,
    selectorDisabled,
    selectorEmpty,
    modalOpen: advertiserSelectorOpen,
    openSelector: openAdvertiserSelector,
    closeSelector: closeAdvertiserSelector,
    handleAdvertiserSelect,
  } = useGovernedAdvertiserCatalog({
    apiBase: API_BASE_URL,
    role: "client",
    onAdvertiserChange: clearAdvertiserScopedState,
  });
  const effectiveSelectedAdvertiserId = selectedAdvertiserId || null;

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setApprovedPlanCampaigns([]);
      if (!effectiveSelectedAdvertiserId) return;

      try {
        const res = await apiJson(
          `/api/media-campaigns?advertiserId=${encodeURIComponent(effectiveSelectedAdvertiserId)}`
        );
        const rows = Array.isArray(res?.campaigns)
          ? res.campaigns
          : Array.isArray(res?.data)
          ? res.data
          : [];
        if (!cancelled) setApprovedPlanCampaigns(rows);
      } catch (_error) {
        if (!cancelled) setApprovedPlanCampaigns([]);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [effectiveSelectedAdvertiserId]);

  const approvedAssets = useMemo(
    () => (Array.isArray(assets) ? assets.filter((asset) => isApprovedAssetForLibrary(asset)) : []),
    [assets]
  );
  const validPackageCount = useMemo(
    () =>
      packages.filter((pkg) => pkg?.validationResults?.valid === true).length,
    [packages]
  );
  const advertiserDisplayName =
    String(resolvedAdvertiser?.name || resolvedAdvertiser?.advertiser || "").trim() ||
    selectedAdvertiserName ||
    "Select Advertiser";
  const approvedPlanContext = useMemo(
    () => buildApprovedPlanContext(approvedPlanCampaigns),
    [approvedPlanCampaigns]
  );
  const approvedPlatformKeys = approvedPlanContext.approvedPlatformKeys;
  const inventoryRows = useMemo(
    () =>
      buildInventoryRows({
        assets: approvedAssets,
        advertiserName: advertiserDisplayName,
        packageDetailsById,
        draftDetailsById,
        approvedPlatformKeys,
        liveDeliveryByAssetId: liveCreativeDeliveryByAssetId,
        unresolvedCreativeDelivery,
      }),
    [
      advertiserDisplayName,
      approvedAssets,
      draftDetailsById,
      packageDetailsById,
      unresolvedCreativeDelivery,
      approvedPlatformKeys,
      liveCreativeDeliveryByAssetId,
    ]
  );
  const platformFilterOptions = useMemo(
    () =>
      Array.from(new Set(approvedPlatformKeys.map((key) => platformLabelFromKey(key)).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [approvedPlatformKeys]
  );
  const creativeTypeOptions = useMemo(
    () =>
      Array.from(new Set(inventoryRows.map((row) => row.creativeType).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [inventoryRows]
  );
  const statusOptions = useMemo(
    () =>
      Array.from(new Set(inventoryRows.map((row) => row.status).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [inventoryRows]
  );
  useEffect(() => {
    if (platformFilter === "all") return;
    if (platformFilterOptions.includes(platformFilter)) return;
    setPlatformFilter("all");
  }, [platformFilter, platformFilterOptions]);
  const filteredInventoryRows = useMemo(() => {
    const query = String(searchQuery || "").trim().toLowerCase();
    const fromTime = updatedFrom ? new Date(`${updatedFrom}T00:00:00`).getTime() : null;
    const toTime = updatedTo ? new Date(`${updatedTo}T23:59:59`).getTime() : null;

    return inventoryRows.filter((row) => {
      if (platformFilter !== "all" && !(row.platformLabels || []).includes(platformFilter)) return false;
      if (creativeTypeFilter !== "all" && row.creativeType !== creativeTypeFilter) return false;
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (liveOnly && !hasLiveDeliverySignal(row.metrics)) return false;
      if (query) {
        const haystack = [
          row.assetName,
          row.advertiser,
          row.creativeType,
          row.creativeFormat,
          row.dimensions,
          ...(row.platformLabels || []),
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      if (fromTime || toTime) {
        const updatedTime = row.lastUpdated ? new Date(row.lastUpdated).getTime() : null;
        if (!updatedTime) return false;
        if (fromTime && updatedTime < fromTime) return false;
        if (toTime && updatedTime > toTime) return false;
      }
      return true;
    });
  }, [inventoryRows, searchQuery, platformFilter, creativeTypeFilter, statusFilter, updatedFrom, updatedTo, liveOnly]);
  const selectedInventoryRow =
    filteredInventoryRows.find((row) => String(row.assetId) === String(selectedInventoryAssetId)) ||
    filteredInventoryRows[0] ||
    null;
  const previewModalRow =
    filteredInventoryRows.find((row) => toAssetKey(row.assetId) === toAssetKey(previewModalAssetId)) ||
    inventoryRows.find((row) => toAssetKey(row.assetId) === toAssetKey(previewModalAssetId)) ||
    null;
  const previewModalIndex = previewModalRow
    ? filteredInventoryRows.findIndex(
        (row) => toAssetKey(row.assetId) === toAssetKey(previewModalRow.assetId)
      )
    : -1;
  const previousPreviewModalRow =
    previewModalIndex > 0 ? filteredInventoryRows[previewModalIndex - 1] : null;
  const nextPreviewModalRow =
    previewModalIndex >= 0 && previewModalIndex < filteredInventoryRows.length - 1
      ? filteredInventoryRows[previewModalIndex + 1]
      : null;
  const hasActiveFilters = Boolean(
    searchQuery.trim() ||
      platformFilter !== "all" ||
      creativeTypeFilter !== "all" ||
      statusFilter !== "all" ||
      liveOnly ||
      updatedFrom ||
      updatedTo
  );
  const gridEmptyState = !effectiveSelectedAdvertiserId
    ? "Select an advertiser to load creative inventory."
    : loading
    ? "Loading trafficking history..."
    : !inventoryRows.length
    ? "No approved creatives are available for this advertiser yet."
    : "No creatives match the current filters.";
  const selectedRowMetric = selectedInventoryRow
    ? getPrimaryMetricPresentation(selectedInventoryRow.metrics)
    : null;

  const openPreviewModalAsset = (assetId) => {
    const normalizedAssetId = String(assetId || "").trim();
    setPreviewModalAssetId(normalizedAssetId);
    if (normalizedAssetId) {
      setSelectedInventoryAssetId(normalizedAssetId);
    }
  };

  const stepPreviewModal = (direction) => {
    if (direction === "previous" && previousPreviewModalRow?.assetId != null) {
      openPreviewModalAsset(previousPreviewModalRow.assetId);
      return;
    }
    if (direction === "next" && nextPreviewModalRow?.assetId != null) {
      openPreviewModalAsset(nextPreviewModalRow.assetId);
    }
  };

  useEffect(() => {
    if (!previewModalAssetId) return undefined;
    const handleKeyDown = (event) => {
      const targetTagName = String(event.target?.tagName || "").toLowerCase();
      const isTypingTarget =
        event.target?.isContentEditable ||
        ["input", "textarea", "select"].includes(targetTagName);

      if (event.key === "Escape") {
        setPreviewModalAssetId("");
        return;
      }
      if (isTypingTarget) return;
      if (event.key === "ArrowLeft" && previousPreviewModalRow?.assetId != null) {
        event.preventDefault();
        openPreviewModalAsset(previousPreviewModalRow.assetId);
      }
      if (event.key === "ArrowRight" && nextPreviewModalRow?.assetId != null) {
        event.preventDefault();
        openPreviewModalAsset(nextPreviewModalRow.assetId);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [previewModalAssetId, previousPreviewModalRow, nextPreviewModalRow]);



  useEffect(() => {
    const controller = new AbortController();
    clearAdvertiserScopedState();
    const scopeEpoch = advertiserScopeEpochRef.current;

    if (!effectiveSelectedAdvertiserId) {
      setLoading(false);
      return () => controller.abort();
    }

    const isCurrentScope = () =>
      !controller.signal.aborted && scopeEpoch === advertiserScopeEpochRef.current;

    const run = async () => {
      setLoading(true);
      try {
        const [assetRes, liveDeliveryRes, packageRes, draftRes] = await Promise.all([
          apiJson(`/api/creative-assets?advertiserId=${effectiveSelectedAdvertiserId}`, {
            signal: controller.signal,
          }),
          apiJsonOptional(
            `/api/creative-assets/live-delivery?advertiserId=${effectiveSelectedAdvertiserId}`,
            { metricsByAssetId: {} },
            { signal: controller.signal }
          ),
          /* Cortex: trafficking packages and deployment drafts are operator
             surfaces (CREATIVE_BETA_INTERNAL_ACCESS); this page reads none. */
          Promise.resolve({ data: { packages: [] } }),
          Promise.resolve({ data: { drafts: [] } }),
        ]);
        if (!isCurrentScope()) return;

        // Only the client's own SOURCE creatives enter state. Cphere-generated
        // artifacts (preview derivatives, thumbnails, exports) are internal
        // storage details — filtering once here keeps them out of the count,
        // the table, search, filters, labels and downloads alike.
        const nextAssets = filterClientVisibleCreativeAssets(assetRes.data);
        const nextPackages = packageRes.data?.packages || [];
        const nextDrafts = draftRes.data?.drafts || [];
        const nextLiveDeliveryByAssetId = normalizeCreativeLiveDeliveryMap(liveDeliveryRes);
        const nextUnresolvedCreativeDelivery = normalizeUnresolvedCreativeDelivery(liveDeliveryRes);
        const [packageDetailResults, draftDetailResults] = await Promise.all([
          Promise.all(
            nextPackages.map(async (pkg) => {
              try {
                const detail = await apiJson(`/api/creative-trafficking/packages/${pkg.id}`, {
                  signal: controller.signal,
                });
                return [String(pkg.id), detail.data];
              } catch (detailError) {
                if (controller.signal.aborted || detailError?.name === "AbortError") {
                  throw detailError;
                }
                return [String(pkg.id), null];
              }
            })
          ),
          Promise.all(
            nextDrafts.map(async (draft) => {
              try {
                const detail = await apiJson(
                  `/api/creative-trafficking/deployment-drafts/${draft.id}`,
                  { signal: controller.signal }
                );
                return [String(draft.id), detail.data];
              } catch (detailError) {
                if (controller.signal.aborted || detailError?.name === "AbortError") {
                  throw detailError;
                }
                return [String(draft.id), null];
              }
            })
          ),
        ]);
        if (!isCurrentScope()) return;

        const packageDetails = Object.fromEntries(packageDetailResults);
        const draftDetails = Object.fromEntries(draftDetailResults);

        setAssets(nextAssets);
        setPackages(nextPackages);
        setLiveCreativeDeliveryByAssetId(nextLiveDeliveryByAssetId);
        setUnresolvedCreativeDelivery(nextUnresolvedCreativeDelivery);
        setPackageDetailsById(packageDetails);
        setDraftDetailsById(draftDetails);
      } catch (err) {
        if (!isCurrentScope() || err?.name === "AbortError") return;
        setError(String(err?.message || "Failed to load trafficking history."));
      } finally {
        if (isCurrentScope()) setLoading(false);
      }
    };

    run();
    return () => {
      controller.abort();
    };
  }, [clearAdvertiserScopedState, effectiveSelectedAdvertiserId]);

  useEffect(() => {
    if (!filteredInventoryRows.length) {
      if (selectedInventoryAssetId) setSelectedInventoryAssetId("");
      return;
    }
    if (!filteredInventoryRows.some((row) => String(row.assetId) === String(selectedInventoryAssetId))) {
      setSelectedInventoryAssetId(String(filteredInventoryRows[0].assetId));
    }
  }, [filteredInventoryRows, selectedInventoryAssetId]);

  return (
    <div className="creativestudio-scope smc-page-container creativetrafficking-suite">
      <div className="smc-page-content">
        <CphereHeaderLogo
          alt="Cphere"
          className="smc-poweredby-logo"
          onClick={() => navigate("/dashboard")}
          style={{ cursor: "pointer" }}
        />

        <div className="cphere-governed-hero-selector-stack">
          <div className="smc-hero-container">
            <div className="smc-hero-pill">
              <span className="smc-hero-pill-label cphere-hero-pill-wordmark">Creative</span>
              <span className="smc-hero-pill-tag cphere-hero-pill-tag">Assets</span>
            </div>
            <div className="smc-hero-shimmer-bar" />
          </div>

          <div className="cphere-governed-advertiser-selector-stage">
            <AdvertiserIdentityTrigger
              advertiser={resolvedAdvertiser}
              advertiserName={selectedAdvertiserName}
              apiBase={API_BASE_URL}
              disabled={selectorDisabled}
              empty={selectorEmpty}
              loading={advertisersLoading}
              modalOpen={advertiserSelectorOpen}
              onClick={openAdvertiserSelector}
            />
          </div>
        </div>

        {advertiserSelectorOpen ? (
          <GovernedAdvertiserSelectorModal
            advertisers={advertisers}
            selectedAdvertiserId={selectedAdvertiserId}
            selectedAdvertiserName={selectedAdvertiserName}
            apiBase={API_BASE_URL}
            onSelect={handleAdvertiserSelect}
            onClose={closeAdvertiserSelector}
          />
        ) : null}

        <details className="creativetrafficking-disclosure creativetrafficking-secondary-disclosure--outer" open>
          <summary className="creativetrafficking-disclosure-summary">
            <span className="creativetrafficking-disclosure-summary-label">Filters</span>
            {hasActiveFilters ? <span className="creativetrafficking-disclosure-badge">Active</span> : null}
          </summary>
          <div className="creativetrafficking-disclosure-body">
            <div className="creativeassets-filter-row">
              <label className="creativeassets-filter-cell creativeassets-filter-cell--search">
                <span className="creativeassets-filter-label">Search</span>
                <input
                  className="creativeoptimus-input creativeassets-filter-control"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search creative, platform, or type"
                />
              </label>
              <label className="creativeassets-filter-cell">
                <span className="creativeassets-filter-label">Platform</span>
                <div className="creativeassets-filter-select-shell">
                  <select
                    className="creativeoptimus-select creativeassets-filter-control"
                    value={platformFilter}
                    onChange={(event) => setPlatformFilter(event.target.value)}
                  >
                    <option value="all">All platforms</option>
                    {platformFilterOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
              <label className="creativeassets-filter-cell">
                <span className="creativeassets-filter-label">Creative Type</span>
                <div className="creativeassets-filter-select-shell">
                  <select
                    className="creativeoptimus-select creativeassets-filter-control"
                    value={creativeTypeFilter}
                    onChange={(event) => setCreativeTypeFilter(event.target.value)}
                  >
                    <option value="all">All types</option>
                    {creativeTypeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
              <label className="creativeassets-filter-cell">
                <span className="creativeassets-filter-label">Status</span>
                <div className="creativeassets-filter-select-shell">
                  <select
                    className="creativeoptimus-select creativeassets-filter-control"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                  >
                    <option value="all">All statuses</option>
                    {statusOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
              <label className="creativeassets-filter-cell">
                <span className="creativeassets-filter-label">Updated From</span>
                <input
                  className="creativeoptimus-input creativeassets-filter-control"
                  type="date"
                  value={updatedFrom}
                  onChange={(event) => setUpdatedFrom(event.target.value)}
                />
              </label>
              <label className="creativeassets-filter-cell">
                <span className="creativeassets-filter-label">Updated To</span>
                <input
                  className="creativeoptimus-input creativeassets-filter-control"
                  type="date"
                  value={updatedTo}
                  onChange={(event) => setUpdatedTo(event.target.value)}
                />
              </label>
              <div className="creativeassets-filter-cell creativeassets-filter-cell--nav">
                <span className="creativeassets-filter-label">Navigation</span>
                
              </div>
            </div>
          </div>
        </details>

        <section className="mediacampaigns-runs-card creativetrafficking-runs-card">
          <div className="mediacampaigns-runs-header">
            <div className="mediacampaigns-runs-title">Creative Library</div>
            <div className="mediacampaigns-runs-subtitle">
              {filteredInventoryRows.length} visible • {validPackageCount} validated packages
            </div>
          </div>

          <div className="mediacampaigns-filters creativetrafficking-primary-filterrow">
            <div className="mediacampaigns-filter-group mediacampaigns-upcoming-toggle creativetrafficking-live-toggle">
              <label>Live Only</label>
              <input type="checkbox" checked={liveOnly} onChange={(event) => setLiveOnly(event.target.checked)} />
            </div>
          </div>

          {unresolvedCreativeDelivery.length ? (
            <div
              className="mediacampaigns-runs-subtitle creativetrafficking-unresolved-delivery"
              role="note"
              data-testid="creative-unresolved-delivery"
            >
              Some YouTube results are not yet linked to creatives.
            </div>
          ) : null}

          {advertiserCatalogError || error ? (
            <div className="mediacampaigns-error">{advertiserCatalogError || error}</div>
          ) : null}
          {loading ? <div className="mediacampaigns-loading">Loading trafficking history...</div> : null}

          {filteredInventoryRows.length ? (
            <div className="mediacampaigns-table-wrapper creativetrafficking-table-wrapper">
              <div
                className="mediacampaigns-table-scroll creativetrafficking-table-scroll"
                role="region"
                aria-label="Creative library"
                tabIndex={0}
              >
                <table className="mediacampaigns-table creativetrafficking-table">
                  <colgroup>
                    <col className="creativetrafficking-col-preview" />
                    <col className="creativetrafficking-col-creative" />
                    <col className="creativetrafficking-col-type" />
                    <col className="creativetrafficking-col-platforms" />
                    <col className="creativetrafficking-col-status" />
                    <col className="creativetrafficking-col-spend" />
                    <col className="creativetrafficking-col-impressions" />
                    <col className="creativetrafficking-col-kpi" />
                    <col className="creativetrafficking-col-updated" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="creativetrafficking-col-preview">Preview</th>
                      <th>Creative</th>
                      <th>Type</th>
                      <th>Platforms</th>
                      <th>Status</th>
                      <th>Spend</th>
                      <th>Impressions</th>
                      <th className="creativetrafficking-col-kpi">KPI</th>
                      <th className="creativetrafficking-col-updated">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInventoryRows.map((row) => {
                      const isActive = String(row.assetId) === String(selectedInventoryRow?.assetId || "");
                      const primaryMetric = getPrimaryMetricPresentation(row.metrics);
                      return (
                        <tr
                          key={row.assetId}
                          className={`mediacampaigns-row creativetrafficking-row ${isActive ? "is-selected" : ""}`}
                          onClick={() => {
                            setSelectedInventoryAssetId(String(row.assetId));
                            openPreviewModalAsset(row.assetId);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setSelectedInventoryAssetId(String(row.assetId));
                              openPreviewModalAsset(row.assetId);
                            }
                          }}
                          tabIndex={0}
                        >
                          <td className="creativetrafficking-cell-preview" data-label="Preview">
                            <div
                              className="creativetrafficking-preview-cell"
                              style={(() => {
                                const ar = getCreativeAspectRatio(row.width, row.height);
                                return ar ? { "--preview-ar": ar } : undefined;
                              })()}
                            >
                              <CreativePreviewTile
                                assetName={row.assetName}
                                sourceUrl={resolveCreativePreviewSourceUrl(row)}
                                fallbackApiPath={getCreativeAssetStreamPaths(row.asset)}
                                posterUrl={row.thumbnailUrl}
                                mimeType={row.previewMimeType}
                                creativeType={row.creativeType}
                                dimensions={row.dimensions}
                                className="creativetrafficking-preview-media"
                                loadingLabel="Loading Thumb"
                                thumbnailMode
                                thumbnailTimeoutMs={15000}
                              />
                            </div>
                          </td>
                          <td className="creativetrafficking-cell-creative" data-label="Creative">
                            <div className="creativetrafficking-creative-cell-body">
                              {/* The name truncates to its own column at desktop
                                  widths; the complete name stays available here
                                  and in the preview/detail view. */}
                              <div className="creativetrafficking-sheet-title" title={row.assetName}>
                                {row.assetName}
                              </div>
                              <div className="creativetrafficking-sheet-sub">
                                <strong>{row.versionLabel}</strong>
                                {row.dimensions && row.dimensions !== "Flexible" ? ` · ${row.dimensions}` : ""}
                              </div>
                            </div>
                          </td>
                          <td data-label="Type">{row.creativeType}</td>
                          <td data-label="Platforms">
                            <div className="creativetrafficking-platform-cell-stack">
                              <CreativeStudioPlatformHeroes
                                platformKeys={row.platformKeys}
                                platformLabels={row.platformLabels}
                                fallback="—"
                                compact
                              />
                            </div>
                          </td>
                          <td data-label="Status">
                            <span className={getStatusChipClass(row.status)}>{row.status}</span>
                          </td>
                          <td data-label="Spend">
                            <span
                              className={`creativetrafficking-data-value ${
                                row.metrics?.spend == null ? "is-empty" : ""
                              }`}
                            >
                              {formatCurrencyCompact(row.metrics?.spend)}
                            </span>
                          </td>
                          <td data-label="Impressions">
                            <span
                              className={`creativetrafficking-data-value ${
                                row.metrics?.impressions == null ? "is-empty" : ""
                              }`}
                            >
                              {formatIntegerCompact(row.metrics?.impressions)}
                            </span>
                          </td>
                          <td className="creativetrafficking-cell-kpi" data-label="KPI">
                            <div className="creativetrafficking-kpi-cell">
                              <span className={`creativetrafficking-kpi-pill ${primaryMetric.tone}`}>
                                <strong>{primaryMetric.label}</strong>
                                <span>{primaryMetric.value}</span>
                              </span>
                            </div>
                          </td>
                          <td className="creativetrafficking-cell-updated" data-label="Updated">
                            <div className="creativetrafficking-updated-cell">
                              <span className="creativetrafficking-updated-value">
                                {formatDate(row.lastUpdated)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="mediacampaigns-empty creativetrafficking-empty-state">
              <strong>
                {!effectiveSelectedAdvertiserId
                  ? "Choose an advertiser"
                  : hasActiveFilters
                    ? "No matching creatives"
                    : "No creatives available"}
              </strong>
              <span>{gridEmptyState}</span>
            </div>
          )}
        </section>

        <details className="creativetrafficking-secondary-disclosure creativetrafficking-secondary-disclosure--outer">
          <summary className="creativetrafficking-disclosure-summary">
            <span className="creativetrafficking-disclosure-summary-label">Selected Creative</span>
            <span className="creativetrafficking-disclosure-badge">
              {selectedInventoryRow ? selectedInventoryRow.assetName : "None selected"}
            </span>
          </summary>
          <div className="creativetrafficking-disclosure-body">
            <section className="creativetrafficking-inline-detail creativetrafficking-inline-detail--card">
              {selectedInventoryRow ? (
                <>
                  <div className="creativetrafficking-inline-detail-head">
                    <div>
                      <div className="creativetrafficking-surface-label">Selected Creative</div>
                      <h3 className="creativetrafficking-detail-title">{selectedInventoryRow.assetName}</h3>
                      <div className="creativetrafficking-detail-subtitle">
                        {selectedInventoryRow.creativeType} · {selectedInventoryRow.versionLabel}
                        {selectedInventoryRow.dimensions && selectedInventoryRow.dimensions !== "Flexible"
                          ? ` · ${selectedInventoryRow.dimensions}`
                          : ""}
                      </div>
                    </div>
                    <span className={getStatusChipClass(selectedInventoryRow.status)}>
                      {selectedInventoryRow.status}
                    </span>
                  </div>

                  <div className="creativetrafficking-inline-detail-grid">
                    <div className="creativetrafficking-detail-preview">
                      <CreativePreviewTile
                        assetName={selectedInventoryRow.assetName}
                        sourceUrl={resolveCreativePreviewSourceUrl(selectedInventoryRow)}
                        fallbackApiPath={getCreativeAssetStreamPaths(selectedInventoryRow.asset)}
                        posterUrl={selectedInventoryRow.thumbnailUrl}
                        mimeType={selectedInventoryRow.previewMimeType}
                        creativeType={selectedInventoryRow.creativeType}
                        dimensions={selectedInventoryRow.dimensions}
                        className="creativetrafficking-detail-media"
                        fallbackClassName="creativetrafficking-detail-fallback"
                        controls
                      />
                    </div>

                    <div className="creativetrafficking-inline-detail-stack">
                      <div className="creativetrafficking-detail-metrics">
                        <div className="creativetrafficking-detail-metric">
                          <span>Spend</span>
                          <strong>{formatCurrencyCompact(selectedInventoryRow.metrics?.spend)}</strong>
                        </div>
                        <div className="creativetrafficking-detail-metric">
                          <span>Impressions</span>
                          <strong>{formatIntegerCompact(selectedInventoryRow.metrics?.impressions)}</strong>
                        </div>
                        <div className="creativetrafficking-detail-metric">
                          <span>Primary KPI</span>
                          <strong className={selectedRowMetric?.tone === "is-empty" ? "is-empty" : ""}>
                            {selectedRowMetric ? `${selectedRowMetric.label} ${selectedRowMetric.value}` : "—"}
                          </strong>
                        </div>
                      </div>

                      {/*
                        Delivery by platform: every contribution the asset
                        earned, each summed once from its own source rows and
                        stated in that platform's vocabulary. Historical
                        evidence only — it says nothing about what is running
                        now, approval, or campaign lifecycle.
                      */}
                      {selectedInventoryRow.metrics?.pendingLinkPlatformKeys?.length ? (
                        <div className="creativetrafficking-detail-section">
                          <div className="creativetrafficking-detail-section-title">Results Pending Link</div>
                          <dl className="creativetrafficking-detail-list">
                            {selectedInventoryRow.metrics.pendingLinkPlatformKeys.map((platformKey) => (
                              <div key={platformKey}>
                                <dt>{platformLabelFromKey(platformKey) || platformKey}</dt>
                                <dd>Results exist for this advertiser but are not yet linked to a creative. Nothing is attributed until the link is verified.</dd>
                              </div>
                            ))}
                          </dl>
                        </div>
                      ) : null}

                      {Array.isArray(selectedInventoryRow.metrics?.contributions) &&
                      selectedInventoryRow.metrics.contributions.length ? (
                        <div className="creativetrafficking-detail-section">
                          <div className="creativetrafficking-detail-section-title">Delivery by Platform</div>
                          <dl className="creativetrafficking-detail-list">
                            {selectedInventoryRow.metrics.contributions.map((contribution, index) => {
                              const kpi = describeContributionKpi(contribution);
                              const platformLabel =
                                platformLabelFromKey(contribution.platformKey) || contribution.platformKey || "Platform";
                              const key = `${contribution.platformKey || "platform"}-${contribution.campaignId || index}`;
                              return (
                                <React.Fragment key={key}>
                                  <div>
                                    <dt>{platformLabel}{contribution.campaignName ? ` · ${contribution.campaignName}` : ""}</dt>
                                    <dd>
                                      {formatCurrencyCompact(contribution.spend)} · {formatIntegerCompact(contribution.impressions)} impr.
                                      {contribution.videoViews != null ? ` · ${formatIntegerCompact(contribution.videoViews)} views` : ""}
                                      {contribution.videoCompletions != null ? ` · ${formatIntegerCompact(contribution.videoCompletions)} completed` : ""}
                                      {kpi ? ` · ${kpi.label} ${kpi.value}` : " · no rate reported"}
                                    </dd>
                                  </div>
                                  {contribution.firstDate || contribution.lastDate ? (
                                    <div>
                                      <dt>Observed</dt>
                                      <dd>
                                        {contribution.firstDate || "—"} → {contribution.lastDate || "—"}
                                        {contribution.sourceLabel ? ` · ${contribution.sourceLabel}` : ""}
                                      </dd>
                                    </div>
                                  ) : null}
                                </React.Fragment>
                              );
                            })}
                          </dl>
                        </div>
                      ) : null}

                      <div className="creativetrafficking-detail-section">
                        <div className="creativetrafficking-detail-section-title">Platforms Running</div>
                        <div className="creativetrafficking-platform-cell-stack">
                          <CreativeStudioPlatformHeroes
                            platformKeys={selectedInventoryRow.platformKeys}
                            platformLabels={selectedInventoryRow.platformLabels}
                            fallback="—"
                          />
                        </div>
                      </div>

                      <div className="creativetrafficking-detail-section">
                        <div className="creativetrafficking-detail-section-title">Asset Metadata</div>
                        <dl className="creativetrafficking-detail-list">
                          <div><dt>Creative Type</dt><dd>{selectedInventoryRow.creativeType}</dd></div>
                          <div><dt>Dimensions</dt><dd>{selectedInventoryRow.dimensions}</dd></div>
                          <div><dt>Aspect Ratio</dt><dd>{selectedInventoryRow.aspectRatio}</dd></div>
                          <div><dt>Orientation</dt><dd>{selectedInventoryRow.orientation}</dd></div>
                          <div><dt>Format</dt><dd>{selectedInventoryRow.creativeFormat}</dd></div>
                          <div><dt>Version</dt><dd>{selectedInventoryRow.versionLabel}</dd></div>
                          <div><dt>Last Updated</dt><dd>{formatDate(selectedInventoryRow.lastUpdated)}</dd></div>
                          <div><dt>Approval</dt><dd>{selectedInventoryRow.approvalLabel}</dd></div>
                        </dl>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="creativetrafficking-empty-state">
                  <strong>{effectiveSelectedAdvertiserId ? "Select a creative" : "Awaiting scope"}</strong>
                  <span>
                    {effectiveSelectedAdvertiserId
                      ? "Choose a row to inspect preview, metadata, platform mapping, and trafficking status."
                      : "Pick an advertiser to unlock the inspection panel."}
                  </span>
                </div>
              )}
            </section>
          </div>
        </details>







        {previewModalRow ? (
          <div
            className="creativetrafficking-preview-modal-overlay"
            onClick={() => setPreviewModalAssetId("")}
            role="presentation"
          >
            <div
              className="creativetrafficking-preview-modal"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={`${previewModalRow.assetName} preview`}
            >
              <div className="creativetrafficking-preview-modal-header">
                <div className="creativetrafficking-preview-modal-title-block">
                  <div className="creativetrafficking-preview-modal-title">{previewModalRow.assetName}</div>
                  <div className="creativetrafficking-preview-modal-subtitle">
                    <strong>{previewModalRow.versionLabel}</strong>
                    {previewModalRow.dimensions && previewModalRow.dimensions !== "Flexible"
                      ? ` · ${previewModalRow.dimensions}`
                      : ""}
                    {previewModalRow.creativeType ? ` · ${previewModalRow.creativeType}` : ""}
                  </div>
                </div>
                <div className="creativetrafficking-preview-modal-header-actions">
                  <button
                    type="button"
                    className="creativetrafficking-preview-modal-close"
                    onClick={() => setPreviewModalAssetId("")}
                  >
                    CLOSE
                  </button>
                </div>
              </div>

              <div className="creativetrafficking-preview-modal-body">
                <div className="creativetrafficking-preview-modal-media">
                  {previousPreviewModalRow ? (
                    <button
                      type="button"
                      className="creativetrafficking-preview-modal-nav creativetrafficking-preview-modal-nav--left"
                      onClick={() => stepPreviewModal("previous")}
                      aria-label="Previous creative"
                    >
                      <span aria-hidden="true">‹</span>
                    </button>
                  ) : null}
                  <CreativePreviewTile
                    assetName={previewModalRow.assetName}
                    sourceUrl={resolveCreativePreviewSourceUrl(previewModalRow)}
                    fallbackApiPath={getCreativeAssetStreamPaths(previewModalRow.asset)}
                    posterUrl={previewModalRow.thumbnailUrl}
                    mimeType={previewModalRow.previewMimeType}
                    creativeType={previewModalRow.creativeType}
                    dimensions={previewModalRow.dimensions}
                    className="creativetrafficking-preview-modal-media-asset"
                    fallbackClassName="creativetrafficking-preview-modal-fallback"
                    controls
                  />
                  {nextPreviewModalRow ? (
                    <button
                      type="button"
                      className="creativetrafficking-preview-modal-nav creativetrafficking-preview-modal-nav--right"
                      onClick={() => stepPreviewModal("next")}
                      aria-label="Next creative"
                    >
                      <span aria-hidden="true">›</span>
                    </button>
                  ) : null}
                </div>

                <div className="creativetrafficking-preview-modal-meta">
                  <span className={getStatusChipClass(previewModalRow.status)}>
                    {previewModalRow.status}
                  </span>
                  <div className="creativetrafficking-platform-cell-stack">
                    <CreativeStudioPlatformHeroes
                      platformKeys={previewModalRow.platformKeys}
                      platformLabels={previewModalRow.platformLabels}
                      fallback="—"
                      compact
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CreativeAssets;
